import { Link } from '@tanstack/react-router';

import type { App } from '@keboola/api-client/developerPortal/types';
import { Card } from '@keboola/design';

import { getAppTitle } from '../helpers';

import { AppIcon } from './AppIcon';
import { ComponentBadges } from './ComponentBadges';

type Props = {
  app: Pick<App, 'id' | 'name' | 'shortDescription' | 'icon' | 'uiOptions'> & {
    type?: App['type'];
  };
  /** Off for the homepage's curated row: no `type`, and well-known names read better bare. */
  showType?: boolean;
  /** `h2` under the catalog's page `h1`, `h3` under a homepage section `h2` — a skipped level fails a11y. */
  titleAs?: 'h2' | 'h3';
};

/** One component in a catalog grid; the whole card is the link. */
export function AppCard({ app, showType = true, titleAs = 'h3' }: Props) {
  const title =
    showType && app.type !== undefined ? getAppTitle({ name: app.name, type: app.type }) : app.name;

  return (
    <Link
      to="/components/$appId"
      params={{ appId: app.id }}
      className="tw:flex tw:flex-col tw:gap-3 tw:rounded-lg tw:border tw:border-border tw:bg-card tw:p-5 tw:no-underline tw:transition-shadow tw:hover:shadow-md"
    >
      <AppIcon app={app} size={48} />

      {/* `Card.Title`: `design/no-raw-heading-on-page` reserves raw headings for `PageHeader`.
          `text-foreground` overrides the inherited link colour. */}
      <Card.Title as={titleAs} className="tw:text-base tw:text-foreground">
        {title}
      </Card.Title>

      {app.shortDescription !== undefined && app.shortDescription !== '' && (
        <p className="tw:m-0 tw:line-clamp-3 tw:text-sm tw:text-muted-foreground">
          {app.shortDescription}
        </p>
      )}

      {/* `mt-auto` keeps badge rows aligned across cards of uneven height. */}
      <div className="tw:mt-auto">
        <ComponentBadges app={{ id: app.id, uiOptions: app.uiOptions }} />
      </div>
    </Link>
  );
}
